import {
  Bleed,
  Box,
  Button,
  Heading,
  HiddenVisually,
  IconChevron,
  Inline,
  Stack,
} from 'braid-design-system';

import { type Jobs } from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import { useNavigate, useParams } from 'react-router';
import { CompanyReviewAISummarySection } from '../../components/salaries/companyReviewAISummarySection/CompanyReviewAISummarySection';
import { OpenJobsSection } from '../../components/salaries/openJobsSection/OpenJobsSection';
import { SalariesByRoleSection } from '../../components/salaries/salariesByRoleSection/SalariesByRolesSection';
import { SalariesListingSection } from '../../components/salaries/salariesListingSection/SalariesListingSection';
import { SalarySatisfactionSection } from '../../components/salaries/salarySatisfactionSection/SalarySatisfactionSection';
import translations from './.vocab';
import type { SalariesTabModel } from './salariesTabModel';

interface SalaryPageByRoleProps {
  getPaginatedJobs: (organisationId: string, page?: number) => Promise<Jobs>;
  model: SalariesTabModel;
  trackJobsPaginationClicked: ({
    searchTotalCount,
    pageNumber,
  }: {
    pageNumber: number;
    searchTotalCount?: number;
  }) => void;
}

export const SalariesPageByRole = ({
  model,
  getPaginatedJobs,
  trackJobsPaginationClicked,
}: SalaryPageByRoleProps) => {
  const { t } = useTranslations(translations);
  const { companyName } = model;
  const params = useParams();
  const navigate = useNavigate();
  const navigateBackSalaryHub = () => {
    void navigate(`/companies/${params.companySlug}/salaries`);
  };
  const mockRoleName = 'role-alias'; // This should be replaced with the actual role name from the model or params
  return (
    <>
      <HiddenVisually>
        <Heading level="1">
          {t('role salary at companyName', {
            role: mockRoleName,
            companyName,
          })}
        </Heading>
      </HiddenVisually>
      <Box paddingX={['none', 'gutter']}>
        <Box paddingTop="small" paddingBottom="large">
          <Inline align="left" space={'small'}>
            <Bleed left="large">
              <Button
                tone="neutral"
                variant="transparent"
                onClick={navigateBackSalaryHub}
                size="small"
              >
                <IconChevron direction="left" />
                {t('Back to all companyName salaries', {
                  companyName,
                })}
              </Button>
            </Bleed>
          </Inline>
        </Box>
        <Stack space={'xlarge'}>
          <SalariesByRoleSection model={model} />
          {model.jobs && (
            <OpenJobsSection
              model={model}
              getPaginatedJobs={getPaginatedJobs}
              trackJobsPaginationClicked={trackJobsPaginationClicked}
            />
          )}
          {!!model.salarySatisfactionRating && (
            <SalarySatisfactionSection model={model} />
          )}
          {model.companyReviewsAISummary && (
            <CompanyReviewAISummarySection model={model} />
          )}
          <SalariesListingSection model={model} isSalariesByRole={true} />
        </Stack>
      </Box>
    </>
  );
};
